import { inject, Injectable, Signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AggregateQueryInput, CampaignType, ComparatorOperators, CreateValueChainGQL, CreateValueChainMutation, DeleteValueChainGQL, DeleteValueChainMutation, DownloadActivityValueChainGQL, DownloadActivityValueChainMutation, FilterStructure, GetActivityValueChainCampaignsGQL, GetActivityValueChainCampaignsQuery, GetActivityValueChainGQL, GetActivityValueChainQuery, OrderTypeInput, UpdateActivityValueChainGQL, UpdateActivityValueChainMutation } from '../../../graphql-planner-tool/generated';
import { Store } from '@ngrx/store';
import { selectFiltersBE } from '../store/activity-value-chain/activity-value-chain.reducer';

@Injectable({
    providedIn: 'root'
})
export class ActivityValueChainService {
    private store: Store = inject(Store);
    filtersBE: Signal<FilterStructure[]> = this.store.selectSignal(selectFiltersBE)
    private GetActivityValueChainGQL: GetActivityValueChainGQL = inject(GetActivityValueChainGQL);
    private GetActivityValueChainCampaignsGQL: GetActivityValueChainCampaignsGQL = inject(GetActivityValueChainCampaignsGQL);
    private UpdateActivityValueChainGQL: UpdateActivityValueChainGQL = inject(UpdateActivityValueChainGQL);
    private DeleteValueChainGQL: DeleteValueChainGQL = inject(DeleteValueChainGQL);
    private CreateValueChainGQL: CreateValueChainGQL = inject(CreateValueChainGQL);
    private DownloadActivityValueChainGQL: DownloadActivityValueChainGQL = inject(DownloadActivityValueChainGQL);

    getActivityValueChains(campaignId: string, parameters: any): Observable<GetActivityValueChainQuery['activityValueChains']> {
        const query: AggregateQueryInput = this.buildActivityValueChainsQuery(parameters);
        return this.GetActivityValueChainGQL.fetch({ campaignId, pageRequest: { page: parameters.page, pageSize: parameters.rows }, query }, { fetchPolicy: 'no-cache' }).pipe(
            map(res => {
                if (res.errors?.length) {
                    throw res.errors;
                }

                if (!res.data?.activityValueChains) {
                    throw new Error('Empty response');
                }

                return res.data?.activityValueChains;
            }))
    }

    getActivityValueChainsCampaigns(type: CampaignType): Observable<GetActivityValueChainCampaignsQuery['campaignsByType']> {
        return this.GetActivityValueChainCampaignsGQL.fetch({ type }, { fetchPolicy: 'no-cache' }).pipe(
            map(res => {
                if (res.errors?.length) {
                    throw res.errors;
                }

                if (!res.data?.campaignsByType) {
                    throw new Error('Empty response');
                }

                return res.data?.campaignsByType;
            }))
    }

    updateActivityValueChain(category: string, id: string, macroCategory: string, categoryDescription: string, viewOrder: number): Observable<UpdateActivityValueChainMutation['updateValueChain'] | undefined> {
        return this.UpdateActivityValueChainGQL.mutate({ id, name: category, description: categoryDescription, parentName: macroCategory, viewOrder }, { fetchPolicy: 'no-cache' }).pipe(map(res => {
            if (res.errors?.length) {
                throw res.errors;
            }

            if (!res.data?.updateValueChain) {
                throw new Error('Empty response');
            }

            return res.data.updateValueChain;
        }))
    }

    deleteValueChain(id: string): Observable<DeleteValueChainMutation['deleteValueChain'] | undefined> {
        return this.DeleteValueChainGQL.mutate({ id }, { fetchPolicy: 'no-cache' }).pipe(map(res => {
            if (res.errors?.length) {
                throw res.errors;
            }

            if (!res.data?.deleteValueChain) {
                throw new Error('Empty response');
            }

            return res.data?.deleteValueChain;
        }))
    }

    createValueChain(category: string, campaignId: string, macroCategory: string, categoryDescription: string): Observable<CreateValueChainMutation['createNewValueChain'] | undefined> {
        return this.CreateValueChainGQL.mutate({ campaignId, name: category, description: categoryDescription, parentName: macroCategory }, { fetchPolicy: 'no-cache' }).pipe(map(res => {
            if (res.errors?.length) {
                throw res.errors;
            }

            if (!res.data?.createNewValueChain) {
                throw new Error('Empty response');
            }

            return res.data?.createNewValueChain;
        }))
    }

    downloadActivityValueChain(campaignId: string, parameters: any): Observable<DownloadActivityValueChainMutation['downloadActivityValueChainExcel']> {
        return this.DownloadActivityValueChainGQL.mutate(
            { campaignId, page: parameters.page ?? 0, pageSize: parameters.rows ?? 100000 },
            { fetchPolicy: 'no-cache' }
        ).pipe(map(res => {
            if (res.errors?.length) throw res.errors;
            if (!res.data?.downloadActivityValueChainExcel) throw new Error('Empty response');
            return res.data.downloadActivityValueChainExcel;
        }));
    }

    buildActivityValueChainsQuery(parameters: any): AggregateQueryInput {
        const ret: AggregateQueryInput = { filters: [], orderBy: { field: 'ID', order: OrderTypeInput.Asc } }
        if (parameters?.filters) {
            Object.keys(parameters?.filters).forEach(key => {
                if (parameters?.filters[key]) {
                    const filter = {
                        field: this.filtersBE().find(f => f.key === key)?.filterValue as string,
                        value: parameters?.filters[key],
                        operator: this.filtersBE().find(f => f.key === key)?.operator as ComparatorOperators
                    }
                    ret.filters.push(filter)
                }
            })
        }
        return ret
    }
}
